import React, { useState, useEffect, useRef } from 'react';
import { observationsAPI, evidenceAPI, auditsAPI, departmentsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Upload,
  FileText,
  Image,
  File,
  Loader2,
  ExternalLink,
  Trash2,
  Filter,
} from 'lucide-react';

const EvidencePage = () => {
  const [observations, setObservations] = useState([]);
  const [audits, setAudits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedAudit, setSelectedAudit] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedObservation, setSelectedObservation] = useState(null);
  const [evidenceList, setEvidenceList] = useState({});
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedAudit || selectedDepartment) {
      fetchObservations();
    }
  }, [selectedAudit, selectedDepartment]);

  const fetchInitialData = async () => {
    try {
      const [auditsRes, deptsRes] = await Promise.all([
        auditsAPI.getAll(),
        departmentsAPI.getAll(),
      ]);
      setAudits(auditsRes.data);
      setDepartments(deptsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchObservations = async () => {
    try {
      const params = {};
      if (selectedAudit) params.audit_id = selectedAudit;
      if (selectedDepartment) params.department_id = selectedDepartment;
      
      const res = await observationsAPI.getAll(params);
      setObservations(res.data);
      
      // Fetch evidence for each observation
      const evidencePromises = res.data.map(async (obs) => {
        const evidenceRes = await evidenceAPI.getByObservation(obs.id);
        return { obsId: obs.id, evidence: evidenceRes.data };
      });
      
      const evidenceResults = await Promise.all(evidencePromises);
      const evidenceMap = {};
      evidenceResults.forEach(({ obsId, evidence }) => {
        evidenceMap[obsId] = evidence;
      });
      setEvidenceList(evidenceMap);
    } catch (error) {
      console.error('Error fetching observations:', error);
    }
  };

  const handleUpload = async (file) => {
    if (!selectedObservation) return;

    setUploading(true);
    try {
      await evidenceAPI.upload(selectedObservation.id, file);
      toast.success('Evidence uploaded successfully');
      setUploadDialogOpen(false);
      fetchObservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload evidence');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['png', 'jpg', 'jpeg'].includes(ext)) return <Image size={16} className="text-blue-500" />;
    if (['pdf'].includes(ext)) return <FileText size={16} className="text-red-500" />;
    return <File size={16} className="text-slate-500" />;
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Compliant': 'badge-compliant',
      'Non-Compliant': 'badge-non-compliant',
      'Partially Compliant': 'badge-partial',
      'Pending': 'badge-pending',
      'Not Applicable': 'badge-pending',
    };
    return <Badge className={styles[status] || 'badge-pending'}>{status}</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="evidence-page">
      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-slate-500 mb-1 block">Filter by Audit</Label>
              <Select value={selectedAudit || 'all'} onValueChange={(v) => setSelectedAudit(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-audit">
                  <SelectValue placeholder="All audits" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All audits</SelectItem>
                  {audits.map((audit) => (
                    <SelectItem key={audit.id} value={audit.id}>
                      {audit.audit_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-slate-500 mb-1 block">Filter by Department</Label>
              <Select value={selectedDepartment || 'all'} onValueChange={(v) => setSelectedDepartment(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-department">
                  <SelectValue placeholder="All departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All departments</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="outline"
              onClick={() => {
                setSelectedAudit('');
                setSelectedDepartment('');
              }}
            >
              <Filter size={16} className="mr-2" />
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Observations with Evidence */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Audit Observations & Evidence
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-[#4A5690] mx-auto" />
            </div>
          ) : observations.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <FileText size={40} className="mx-auto mb-3 text-slate-300" />
              <p>No observations found</p>
              <p className="text-sm">Complete audit observations to upload evidence</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Control</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th>Remark</th>
                    <th>Evidence</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {observations.map((obs) => (
                    <tr key={obs.id}>
                      <td>
                        <div className="font-medium text-[#4A5690]">{obs.control_id}</div>
                        <div className="text-xs text-slate-500 truncate max-w-[200px]">
                          {obs.control_title}
                        </div>
                      </td>
                      <td>{obs.department_name || 'N/A'}</td>
                      <td>{getStatusBadge(obs.compliance_status)}</td>
                      <td>
                        <div className="max-w-[200px] truncate text-sm">
                          {obs.audit_remark || '-'}
                        </div>
                      </td>
                      <td>
                        <div className="flex flex-col gap-1">
                          {evidenceList[obs.id]?.map((ev) => (
                            <a
                              key={ev.id}
                              href={`${process.env.REACT_APP_BACKEND_URL}${ev.file_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                            >
                              {getFileIcon(ev.file_name)}
                              <span className="truncate max-w-[120px]">{ev.file_name}</span>
                              <ExternalLink size={12} />
                            </a>
                          ))}
                          {(!evidenceList[obs.id] || evidenceList[obs.id].length === 0) && (
                            <span className="text-sm text-slate-400">No evidence</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedObservation(obs);
                            setUploadDialogOpen(true);
                          }}
                          data-testid={`upload-evidence-${obs.id}`}
                        >
                          <Upload size={14} className="mr-1" />
                          Upload
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Upload Evidence
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedObservation && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-[#4A5690]">
                  {selectedObservation.control_title}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Department: {selectedObservation.department_name}
                </p>
              </div>
            )}

            <div
              className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center cursor-pointer hover:border-[#4A5690]/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={40} className="mx-auto mb-3 text-slate-400" />
              <p className="text-sm text-slate-600">Click to select a file</p>
              <p className="text-xs text-slate-400 mt-1">
                Supported: PDF, DOCX, XLSX, PNG, JPG
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
            </div>

            {uploading && (
              <div className="flex items-center justify-center gap-2 text-[#4A5690]">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Uploading...</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EvidencePage;
